import { useTranslation } from "react-i18next";
import { KPICard } from "@/components/dashboard/v2/KPICard";
import { KPICardSkeleton } from "@/components/dashboard/v2/skeletons/KPICardSkeleton";
import { useDashboardV2Store } from "@/store/api/dashboardV2Store";
import { useCurrency } from "@/contexts/CurrencyContext";
import { WIDGET_REGISTRY } from "@/config/dashboardWidgets";

interface DynamicKPICardProps {
  widgetId: string;
  loading: boolean;
}

export function DynamicKPICard({ widgetId, loading }: DynamicKPICardProps) {
  const { t } = useTranslation("adminui2");
  const { formatCurrency } = useCurrency();
  const {
    totalMembers,
    classesToday,
    totalBookings,
    revenue,
    newMembers,
    renewals,
    averageOccupation,
    retentionRate,
  } = useDashboardV2Store();

  const widgetDef = WIDGET_REGISTRY[widgetId];
  
  if (loading) {
    return <KPICardSkeleton />;
  }

  if (!widgetDef) {
    return null;
  }

  const Icon = widgetDef.icon;

  // Map widget ID to data
  const getKPIData = () => {
    switch (widgetId) {
      case 'kpi-members':
        return {
          title: t("kpiData.members.title"),
          subtitle: t("kpiData.members.subtitle"),
          value: totalMembers,
        };
      case 'kpi-classes':
        return {
          title: t("kpiData.classes.title"),
          subtitle: t("kpiData.classes.subtitle"),
          value: classesToday,
        };
      case 'kpi-bookings':
        return {
          title: t("kpiData.bookings.title"),
          subtitle: t("kpiData.bookings.subtitle"),
          value: totalBookings,
        };
      case 'kpi-revenue':
        return {
          title: t("kpiData.revenue.title"),
          subtitle: t("kpiData.revenue.subtitle"),
          value: formatCurrency(revenue),
        };
      case 'kpi-new-members':
        return {
          title: t("kpiData.newMembers.title"),
          subtitle: t("kpiData.newMembers.subtitle"),
          value: newMembers,
        };
      case 'kpi-renewals':
        return {
          title: t("kpiData.renewals.title"),
          subtitle: t("kpiData.renewals.subtitle"),
          value: renewals,
        };
      case 'kpi-occupation':
        return {
          title: t("kpiData.occupation.title"),
          subtitle: t("kpiData.occupation.subtitle"),
          value: `${averageOccupation}%`,
        };
      case 'kpi-retention':
        return {
          title: t("kpiData.retention.title"),
          subtitle: t("kpiData.retention.subtitle"),
          value: `${retentionRate}%`,
        };
      // Les cinq tuiles factices (taux d'annulation, actifs cette semaine,
      // expirent bientôt, paiements en attente, MRR) ont été retirées du
      // registre : elles renvoyaient des valeurs inventées. Voir le commentaire
      // dans `src/config/dashboardWidgets.ts`.
      default:
        return null;
    }
  };

  const kpiData = getKPIData();

  // Widget connu du registre mais sans source de données : on n'affiche rien
  // plutôt qu'un « 0 » qui passerait pour une mesure.
  if (!kpiData) {
    return null;
  }

  return (
    <KPICard
      title={kpiData.title}
      subtitle={kpiData.subtitle}
      value={kpiData.value}
      icon={<Icon className="h-4 w-4 text-primary" />}
      loading={false}
    />
  );
}
